import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useNavigateRef } from 'hooks/useNavigateRef';

import Main from 'components/Main';
import { StoreState } from 'store/ducks';
import { tryUrlSafeBase64Decode } from 'utils/base64';
import { RocketChatRestAPI } from 'utils/httpAPI';
import { extractBizAccountIdFromChannelId, getBizChatUrl } from 'utils/snippet';
import { socketStore } from 'utils/socket/socketStore';

// /business/:bizId, /business/:bizId/:channelId 진입을 신규 비즈채팅 웹으로 보낸다.
// 신규 웹은 채널 파라미터를 받지 않으므로 비즈계정만 전달한다.
export const BusinessRouteRedirect = () => {
  const { bizId } = useParams();
  useEffect(() => {
    window.location.replace(getBizChatUrl(bizId));
  }, [bizId]);
  return null;
};

// /, /room/:channelId 공통 라우트. 두 경로가 같은 컴포넌트를 쓰므로 채널을 오갈 때 Main이 다시 마운트되지 않는다.
// - bizToken(비즈센터 쿠키)만 있는 세션은 레거시에서 할 수 있는 것이 없으므로 Main을 마운트하지 않고 로그인 페이지로 보낸다.
//   (QR 세션이 끊긴 중고거래 유저가 신규 비즈채팅 웹에 갇히지 않도록 신규 웹으로 보내지 않는다. 로그인 페이지에 비즈채팅 링크가 있다.)
// - 채널에 내가 소유한 비즈계정이 참여하면 신규 웹으로 보내고, 그 외(개인 채널, 고객 측 비즈채널, 확인 실패)는 기존 화면을 보여준다.
//   소유 목록이 소켓 세션으로 이미 받아져 있으면(앱 내 이동) 스토어로 즉시 판단하고,
//   첫 진입(목록 없음)에서만 HTTP로 확인하며 그동안은 Main을 마운트하지 않아 웹소켓이 열리지 않는다.
export const MainRoute = () => {
  const { channelId = '' } = useParams();
  const bizAccountId = useMemo(
    () => (channelId ? extractBizAccountIdFromChannelId(tryUrlSafeBase64Decode(channelId.slice(1))) : undefined),
    [channelId],
  );
  const oidcAccessToken = useSelector((state: StoreState) => state.authStore.oidcAccessToken);
  const storeAccessToken = useSelector((state: StoreState) => state.authStore.storeAccessToken);
  const bizToken = useSelector((state: StoreState) => state.authStore.bizToken);
  const isAuthResolved = useSelector((state: StoreState) => state.authStore.isAuthResolved);
  const ownedBizAccounts = useSelector((state: StoreState) => state.authStore.bizAccount);
  // GetBizAccountsResponse를 받기 전까지는 pending이므로, 이 값이 true일 때만 스토어 목록을 믿을 수 있다.
  const isBizListLoaded = useSelector((state: StoreState) => !state.chatStore.isBizRequestPending);
  const hasUserToken = Boolean(oidcAccessToken || storeAccessToken);
  // 유저 토큰이 저장되면 authStore가 bizToken을 비우므로, bizToken이 남아 있다는 것은 비즈센터 쿠키만 있는 세션이라는 뜻이다.
  const isBizTokenOnly = Boolean(bizToken);
  const navigateRef = useNavigateRef();
  // HTTP로 소유하지 않은 것을 확인한 비즈계정 ID. 채널이 바뀌어 다른 비즈계정이 나오면 다시 확인한다.
  const [notOwnedBizAccountId, setNotOwnedBizAccountId] = useState<number>();

  const ownedByStore =
    bizAccountId !== undefined && isBizListLoaded && ownedBizAccounts.some((item) => item.id === bizAccountId);

  useEffect(() => {
    if (isBizTokenOnly) {
      // 비즈채팅 진입 링크가 있는 로그인 페이지로 보낸다. 온보딩 화면에는 비즈채팅 링크가 없어 온보딩 분기를 타지 않는다.
      navigateRef.current('/login', { replace: true });
    }
  }, [isBizTokenOnly, navigateRef]);

  useEffect(() => {
    if (bizAccountId === undefined || isBizTokenOnly) return;
    if (isBizListLoaded) {
      if (ownedByStore) {
        window.location.replace(getBizChatUrl(bizAccountId));
      }
      return;
    }
    // 토큰은 appInit이 비동기로 스토어에 넣으므로, 값이 생긴 뒤에 소유 확인을 시작한다.
    if (!hasUserToken) return;
    // 이미 소유하지 않은 것으로 확인한 계정이면 소켓 재연결로 목록이 pending으로 되돌아가도 다시 묻지 않는다.
    if (notOwnedBizAccountId === bizAccountId) return;
    let cancelled = false;
    RocketChatRestAPI.getBizAccountIds().then((ownedIds) => {
      if (cancelled) return;
      if (ownedIds !== null && ownedIds.includes(bizAccountId)) {
        window.location.replace(getBizChatUrl(bizAccountId));
      } else {
        setNotOwnedBizAccountId(bizAccountId);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [bizAccountId, isBizTokenOnly, hasUserToken, isBizListLoaded, notOwnedBizAccountId, ownedByStore]);

  if (isBizTokenOnly || ownedByStore) {
    return null;
  }
  // 인증 판정이 끝나기 전에는 경로와 무관하게 숨긴다 (bizToken 세션이 판정 전에 빈 채팅 화면을 보는 것을 막는다).
  // 판정 완료는 되돌아가지 않으므로 한 번 띄운 화면을 이 조건이 다시 숨기는 일은 없다.
  if (!isAuthResolved) {
    return null;
  }
  // HTTP 소유 확인이 진행 중이고 아직 소켓이 열리지 않았으면 숨겨서 레거시 소켓이 열리지 않게 한다.
  // 데드라인을 넘겨 토큰 없이 띄운 화면은 토큰이 늦게 도착하면 확인이 끝날 때까지 다시 숨겨진다 (소켓 방지가 우선).
  // 판정이 끝났는데 토큰이 없으면(리프레시 실패 등) 확인을 시작할 수 없으므로 기존 화면을 보여준다.
  // socketStore.chatSocket은 한 번 열리면 null로 되돌아가지 않는다(재연결은 참조 교체) — 세션 도중 이 조건이 Main을
  // 언마운트하지 않는 근거이므로, onclose에서 null로 정리하도록 바꾸면 이 게이트도 함께 손봐야 한다.
  const isCheckingByHttp =
    bizAccountId !== undefined && !isBizListLoaded && notOwnedBizAccountId !== bizAccountId && hasUserToken;
  if (socketStore.chatSocket === null && isCheckingByHttp) {
    return null;
  }
  return <Main />;
};
